/**
 * Mueller Theme Colors - Single Source of Truth
 * 
 * This file defines all theme colors used throughout the application.
 * Colors are injected as CSS custom properties at runtime, allowing
 * dark mode to work by overriding these properties in .dark-theme class.
 * 
 * Usage:
 * - Import `colors` for MUI theme palette
 * - Import `themeVars` for semantic theme variables
 * - Call `initThemeColors()` early in app initialization
 */

// =============================================================================
// Base Colors (raw color values)
// =============================================================================
export const colors = {
  white: '#FFFFFF',
  black: 'rgba(0, 0, 0, 0.87)',
  translucentBlack: 'rgba(0, 0, 0, 0.54)',
  translucentWhite: 'rgba(255, 255, 255, 0.5)',
  dropShadow: '0 0 0',
  
  // Named colors
  nomad: '#B5ABA2',
  redberry: '#8B0000',
  redberryLight: '#ffd6dd',
  crimson: '#c8102e',
  rollingstone: '#75787b',
  silversand: '#babcbd',
  silverchalice: '#f4f4f4',
  gallery: '#eeeeee',
  good: 'green',
  
  // Additional specific colors
  hyperlinkBlue: '#007bff',
  hyperlinkBlueHover: '#0056b3',
  clearButtonBg: '#FFBDAD',
  clearButtonHoverBg: '#DE350B',
  gridActiveBg: '#DFDFDF',
  mgridformHideText: '#fa6767',
};

// =============================================================================
// Theme Variables (semantic mappings using base colors)
// These map to CSS custom properties
// =============================================================================
export const themeVars = {
  // Primary theme colors
  primaryColor: colors.crimson,
  primaryColorDarker: colors.redberry,
  secondaryColor: colors.silversand,
  secondaryColorDarker: colors.rollingstone,
  tertiaryColor: colors.white,
  tertiaryColorDarker: colors.gallery,
  fourthColor: colors.black,
  fifthColor: colors.silverchalice,
  
  // Font colors
  primaryFontColor: colors.black,
  secondaryFontColor: colors.white,
  tertiaryFontColor: colors.rollingstone,
  errorFontColor: colors.crimson,
  disabledFontColor: colors.rollingstone,
  hyperlinkColor: colors.hyperlinkBlue,
  hyperlinkHoverColor: colors.hyperlinkBlueHover,
  
  // Icon colors
  primaryIconColor: colors.crimson,
  secondaryIconColor: colors.white,
  disabledIconColor: colors.silversand,
  
  // Interaction colors
  hoverColor: colors.gallery,
  defaultBorderColor: colors.silversand,
  
  // Header colors
  primaryHeaderBgColor: colors.silversand,
  primaryHeaderFontColor: colors.white,
  secondaryHeaderBgColor: colors.silverchalice,
  secondaryHeaderFontColor: colors.crimson,
  
  // Global/App colors
  appBgColor: colors.silverchalice,
  pageHeaderBgColor: colors.crimson,
  pageHeaderFontColor: colors.white,
  pageHeaderButtonBgColor: colors.rollingstone,
  pageHeaderButtonBgColorHover: colors.silversand,
  footerFontColor: colors.white,
  footerBgColor: colors.rollingstone,
  
  // Tooltip colors
  tooltipTitleBgColor: colors.crimson,
  tooltipTitleFontColor: colors.white,
  tooltipFontColor: colors.black,
  tooltipBgColor: colors.white,
  tooltipBorderColor: colors.silversand,
  
  // Avatar
  avatarBgColor: colors.crimson,
  avatarFontColor: colors.white,
  
  // Button colors
  buttonFontColor: colors.white,
  buttonBgColor: colors.crimson,
  buttonDisabledBgColor: colors.silversand,
  buttonDisabledFontColor: colors.rollingstone,
  buttonHoverBgColor: colors.redberry,
  buttonHoverFontColor: colors.white,
  buttonSecondaryFontColor: colors.white,
  buttonSecondaryBgColor: colors.rollingstone,
  
  // Card colors
  cardHeaderBgColor: colors.silversand,
  cardHeaderFontColor: colors.white,
  cardRowHover: colors.gallery,
  cardBgColor: colors.white,
  
  // Grid colors
  gridHeaderFontColor: colors.crimson,
  gridColumnHeaderBgColor: colors.silverchalice,
  gridBgColor: colors.white,
  gridBorderColor: colors.silversand,
  gridHoverColor: colors.gallery,
  gridContentFontColor: colors.black,
  gridRowsBgColor: colors.white,
  
  // MGridForm
  mgridformBgColor: colors.white,
  mgridformDisabledElement: colors.silversand,
  mgridformDisabledFontColor: colors.white,
  mgridformShaded: colors.gallery,
  mgridformHeader: colors.black,
  mgridformInvalidElement: colors.redberry,
  
  // Dropdown
  dropdownBgColor: colors.white,
  
  // Toast/Snackbar
  toastErrorBgColor: colors.redberry,
  toastFontColor: colors.white,
  
  // Date picker
  datePickerDateTextColor: colors.white,
  datePickerDateBgColor: colors.silversand,
  datePickerActiveText: colors.crimson,
  datePickerClockBgColor: colors.silverchalice,
  
  // Phoenix Forms
  pnxSectionHeaderBgColor: colors.rollingstone,
  pnxSectionHeaderFontColor: colors.white,
};

// =============================================================================
// CSS Variable Name Mapping
// Maps JS property names to CSS custom property names
// =============================================================================
const cssVarNames = {
  // Base colors
  white: '--white',
  black: '--black',
  translucentBlack: '--translucent-black',
  translucentWhite: '--translucent-white',
  dropShadow: '--drop-shadow',
  nomad: '--nomad',
  redberry: '--redberry',
  redberryLight: '--redberry-light',
  crimson: '--crimson',
  rollingstone: '--rollingstone',
  silversand: '--silversand',
  silverchalice: '--silverchalice',
  gallery: '--gallery',
  good: '--good',
  
  // Theme colors
  primaryColor: '--primary-color',
  primaryColorDarker: '--primary-color-darker',
  secondaryColor: '--secondary-color',
  secondaryColorDarker: '--secondary-color-darker',
  tertiaryColor: '--tertiary-color',
  tertiaryColorDarker: '--tertiary-color-darker',
  fourthColor: '--fourth-color',
  fifthColor: '--fifth-color',
  
  // Font colors
  primaryFontColor: '--primary-font-color',
  secondaryFontColor: '--secondary-font-color',
  tertiaryFontColor: '--tertiary-font-color',
  errorFontColor: '--error-font-color',
  disabledFontColor: '--disabled-font-color',
  hyperlinkColor: '--hyperlink-color',
  hyperlinkHoverColor: '--hyperlink-hover-color',
  
  // Icon colors
  primaryIconColor: '--primary-icon-color',
  secondaryIconColor: '--secondary-icon-color',
  disabledIconColor: '--disabled-icon-color',
  
  // Interaction
  hoverColor: '--hover-color',
  defaultBorderColor: '--default-border-color',
  
  // Headers
  primaryHeaderBgColor: '--primary-header-bg-color',
  primaryHeaderFontColor: '--primary-header-font-color',
  secondaryHeaderBgColor: '--secondary-header-bg-color',
  secondaryHeaderFontColor: '--secondary-header-font-color',
  
  // App
  appBgColor: '--app-bg-color',
  pageHeaderBgColor: '--page-header-bg-color',
  pageHeaderFontColor: '--page-header-font-color',
  pageHeaderButtonBgColor: '--page-header-button-bg-color',
  pageHeaderButtonBgColorHover: '--page-header-button-bg-color-hover',
  footerFontColor: '--footer-font-color',
  footerBgColor: '--footer-bg-color',
  
  // Tooltips
  tooltipTitleBgColor: '--tooltip-title-bg-color',
  tooltipTitleFontColor: '--tooltip-title-font-color',
  tooltipFontColor: '--tooltip-font-color',
  tooltipBgColor: '--tooltip-bg-color',
  tooltipBorderColor: '--tooltip-border-color',
  
  // Avatar
  avatarBgColor: '--avatar-bg-color',
  avatarFontColor: '--avatar-font-color',
  
  // Buttons
  buttonFontColor: '--button-font-color',
  buttonBgColor: '--button-bg-color',
  buttonDisabledBgColor: '--button-disabled-bg-color',
  buttonDisabledFontColor: '--button-disabled-font-color',
  buttonHoverBgColor: '--button-hover-bg-color',
  buttonHoverFontColor: '--button-hover-font-color',
  buttonSecondaryFontColor: '--button-secondary-font-color',
  buttonSecondaryBgColor: '--button-secondary-bg-color',
  
  // Cards
  cardHeaderBgColor: '--card-header-bg-color',
  cardHeaderFontColor: '--card-header-font-color',
  cardRowHover: '--card-row-hover',
  cardBgColor: '--card-bg-color',
  
  // Grid
  gridHeaderFontColor: '--grid-header-font-color',
  gridColumnHeaderBgColor: '--grid-column-header-bg-color',
  gridBgColor: '--grid-bg-color',
  gridBorderColor: '--grid-border-color',
  gridHoverColor: '--grid-hover-color',
  gridContentFontColor: '--grid-content-font-color',
  gridRowsBgColor: '--grid-rows-bg-color',
  
  // MGridForm
  mgridformBgColor: '--mgridform-bg-color',
  mgridformDisabledElement: '--mgridform-disabled-element',
  mgridformDisabledFontColor: '--mgridform-disabled-font-color',
  mgridformShaded: '--mgridform-shaded',
  mgridformHeader: '--mgridform-header',
  mgridformInvalidElement: '--mgridform-invalid-element',
  
  // Dropdown
  dropdownBgColor: '--dropdown-bg-color',
  
  // Toast
  toastErrorBgColor: '--toast-error-bg-color',
  toastFontColor: '--toast-font-color',
  
  // Date picker
  datePickerDateTextColor: '--date-picker-date-text-color',
  datePickerDateBgColor: '--date-picker-date-bg-color',
  datePickerActiveText: '--date-picker-active-text',
  datePickerClockBgColor: '--date-picker-clock-bg-color',
  
  // Phoenix Forms
  pnxSectionHeaderBgColor: '--pnx-section-header-bg-color',
  pnxSectionHeaderFontColor: '--pnx-section-header-font-color',
};

// =============================================================================
// Initialization Function
// Injects base CSS custom properties into :root only
// Theme variable mappings are handled by baseTheme.scss (and overridden by darkTheme.scss)
// =============================================================================
export function initThemeColors() {
  const root = document.documentElement;
  
  // Only set base colors on :root
  // Theme variables (--primary-color, --button-bg-color, etc.) are defined in baseTheme.scss
  // and overridden in darkTheme.scss via the .dark-theme class
  // This preserves CSS cascade so dark mode overrides work correctly
  Object.entries(colors).forEach(([key, value]) => {
    const cssVarName = cssVarNames[key];
    if (cssVarName) {
      root.style.setProperty(cssVarName, value);
    }
  });
  
  // Note: We intentionally do NOT set theme variables on body here
  // because inline styles have higher specificity than CSS class rules,
  // which would break dark mode (.dark-theme overrides in darkTheme.scss)
}

// =============================================================================
// MUI Theme Palette (for use in theme/index.js)
// =============================================================================
export const muiPalette = {
  primary: {
    main: colors.crimson,
    dark: colors.redberry,
    contrastText: colors.white,
  },
  secondary: {
    main: colors.silversand,
    dark: colors.rollingstone,
    contrastText: colors.black,
  },
  error: {
    main: colors.crimson,
  },
  background: {
    default: colors.silverchalice,
    paper: colors.white,
  },
  text: {
    primary: colors.black,
    secondary: colors.rollingstone,
    disabled: colors.rollingstone,
  },
  action: {
    hover: colors.gallery,
    disabled: colors.rollingstone,
    disabledBackground: colors.silversand,
  },
  divider: colors.silversand,
};

export default colors;
